// Job screening (Phase 1b) — edit the JD screening criteria and rank candidates
// (this vacancy's applicants + the talent pool) by keyword match. Shortlist a
// pooled candidate into the role. Rendered inside JobDetail's "Screening" tab.
import { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { Card, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState, UserAvatar } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const scoreColor = (s) => (s >= 70 ? AG.successDark : s >= 40 ? AG.warningDark : AG.n500);

export default function JobScreening({ jobId, openCandidate }) {
  const [data, setData] = useState(null);
  const [skills, setSkills] = useState('');
  const [minYears, setMinYears] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () =>
    api.screenJob(jobId).then((d) => {
      setData(d);
      setSkills((d.job.jdSkills || []).join(', '));
      setMinYears(d.job.minYears ?? '');
    }).catch((e) => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const saveCriteria = async () => {
    setBusy(true); setErr('');
    try {
      await api.updateJob(jobId, { jdSkills: skills, minYears: minYears === '' ? null : Number(minYears) });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const shortlist = async (c) => {
    setBusy(true); setErr('');
    try { await api.shortlistCandidate(jobId, c.id); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!data) return <CenterSpinner />;
  const ranked = data.candidates || [];

  return (
    <div className="space-y-3">
      <Card style={{ padding: 18 }}>
        <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Screening criteria</h3>
        <div className="flex items-end gap-2 flex-wrap">
          <div style={{ flex: 2, minWidth: 220 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Required skills (comma-separated)</span>
            <TextField fullWidth size="small" value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="React, Node, SQL" sx={{ mt: 0.5 }} />
          </div>
          <div style={{ width: 120 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Min years</span>
            <TextField fullWidth size="small" type="number" value={minYears} onChange={(e) => setMinYears(e.target.value)} sx={{ mt: 0.5 }} />
          </div>
          <PrimaryBtn onClick={saveCriteria} disabled={busy}>Save &amp; re-screen</PrimaryBtn>
        </div>
        {err && <p style={{ color: AG.errorDark, fontSize: 13, marginTop: 8 }}>{err}</p>}
      </Card>

      {ranked.length === 0 ? (
        <Card><EmptyState icon={UserAvatar} title="No candidates to screen" desc="Add candidates to the talent pool, then set the required skills above." /></Card>
      ) : (
        <div className="space-y-2">
          {ranked.map((c) => (
            <Card key={c.id} style={{ padding: 14 }}>
              <div className="flex items-center gap-3">
                <UserAvatar name={c.name} size="sm" />
                <div className="flex-1 min-w-0" style={{ cursor: c.jobId ? 'pointer' : 'default' }} onClick={() => c.jobId && openCandidate?.(c.id)}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{c.name} <span style={{ color: AG.n400, fontWeight: 400 }}>· {c.role}{c.jobId ? '' : ' · pool'}</span></div>
                  <div className="flex items-center gap-2" style={{ marginTop: 4, flexWrap: 'wrap' }}>
                    {(c.matched || []).slice(0, 6).map((m) => <Pill key={m} tone="success">{m}</Pill>)}
                    {!c.meetsMinYears && <Pill tone="warning">below min years</Pill>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor(c.matchScore), lineHeight: 1 }}>{c.matchScore}<span style={{ fontSize: 12, color: AG.n400 }}>%</span></div>
                  <div style={{ fontSize: 11, color: AG.n400 }}>match</div>
                </div>
                {!c.jobId && <OutlineBtn onClick={() => shortlist(c)} disabled={busy}>Shortlist</OutlineBtn>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
